import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Button, Badge, Alert, Tabs, Tab } from "react-bootstrap";
import { useAuth } from "../context/AuthContext.js";
import { useLanguage } from "../context/LanguageContext.js";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebase.js";
import { FileText, CheckCircle, Clock, Award } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { t } from "../utils/translations.js";

export default function HealthSafetyPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [quizResults, setQuizResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("documents");

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    if (!user?.uid) return;

    try {
      setLoading(true);

      // Load active documents
      const docsQuery = query(
        collection(db, "healthSafetyDocuments"),
        where("isActive", "==", true)
      );
      const docsSnap = await getDocs(docsQuery);
      const docsData = docsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDocuments(docsData);

      // Load user signatures
      const sigsQuery = query(
        collection(db, "documentSignatures"),
        where("userId", "==", user.uid),
        orderBy("signedAt", "desc")
      );
      const sigsSnap = await getDocs(sigsQuery);
      const sigsData = sigsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSignatures(sigsData);

      // Load quiz results
      const quizQuery = query(
        collection(db, "quizResults"),
        where("userId", "==", user.uid),
        orderBy("completedAt", "desc")
      );
      const quizSnap = await getDocs(quizQuery);
      const quizData = quizSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setQuizResults(quizData);

    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDocumentStatus = (docId, docVersion) => {
    const signature = signatures.find(
      sig => sig.documentId === docId && sig.documentVersion === docVersion
    );
    return signature ? "signed" : "pending";
  };

  const handleViewDocument = (doc) => {
    navigate(`/health-safety/document/${doc.id}`);
  };

  const handleStartTraining = () => {
    navigate("/health-safety/training");
  };

  const lastQuizResult = quizResults[0];

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t(language, "loading")}</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="fw-bold">
            <FileText className="me-2" size={32} />
            {t(language, "healthSafety")}
          </h2>
          <p className="text-muted">
            {t(language, "healthSafetySubtitle")}
          </p>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        {/* Documents Tab */}
        <Tab eventKey="documents" title={t(language, "documents")}>
          <Row className="g-4 mt-2">
            {documents.length === 0 ? (
              <Col xs={12}>
                <Alert variant="info">
                  {t(language, "noDocuments")}
                </Alert>
              </Col>
            ) : (
              documents.map((doc) => {
                const status = getDocumentStatus(doc.id, doc.version);
                const signature = signatures.find(
                  sig => sig.documentId === doc.id && sig.documentVersion === doc.version
                );

                return (
                  <Col key={doc.id} xs={12} md={6} lg={4}>
                    <Card className="h-100 shadow-sm">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <FileText size={40} className="text-primary" />
                          {status === "signed" ? (
                            <Badge bg="success">
                              <CheckCircle size={14} className="me-1" />
                              {t(language, "signed")}
                            </Badge>
                          ) : (
                            <Badge bg="warning" text="dark">
                              <Clock size={14} className="me-1" />
                              {t(language, "pending")}
                            </Badge>
                          )}
                        </div>

                        <Card.Title className="mb-3">
                          {doc.title?.pt || doc.title?.en || "Documento"}
                        </Card.Title>

                        <div className="mb-3">
                          <small className="text-muted">
                            <strong>{t(language, "version")}:</strong> {doc.version}<br />
                            <strong>{t(language, "language")}:</strong> {doc.language === "pt" ? t(language, "portuguese") : t(language, "english")}
                          </small>
                        </div>

                        {signature && (
                          <div className="mb-3 p-2 bg-light rounded">
                            <small>
                              <strong>{t(language, "signedOn")}:</strong><br />
                              {signature.signedAt?.toDate?.().toLocaleString(language === "pt" ? "pt-BR" : "en-NZ") || "N/A"}
                            </small>
                          </div>
                        )}

                        <Button
                          variant={status === "signed" ? "outline-primary" : "primary"}
                          size="sm"
                          onClick={() => handleViewDocument(doc)}
                          className="w-100"
                        >
                          {status === "signed" ? t(language, "viewDocument") : t(language, "openAndSign")}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })
            )}
          </Row>
        </Tab>

        {/* Training Tab */}
        <Tab eventKey="training" title={t(language, "training")}>
          <Row className="mt-3">
            <Col xs={12} lg={6}>
              <Card className="shadow-sm mb-4">
                <Card.Body>
                  <div className="d-flex align-items-center mb-3">
                    <Award size={40} className="text-warning me-3" />
                    <div>
                      <Card.Title className="mb-0">
                        {t(language, "trainingAssessment")}
                      </Card.Title>
                      <small className="text-muted">
                        {t(language, "healthSafetyTraining")}
                      </small>
                    </div>
                  </div>

                  <hr />

                  <div className="mb-3">
                    <p><strong>{t(language, "totalQuestions")}:</strong> 30</p>
                    <p><strong>{t(language, "minimumScore")}:</strong> 85% (26/30 corretas)</p>
                    <p><strong>{t(language, "time")}:</strong> {t(language, "noTimeLimit")}</p>
                    <p><strong>{t(language, "attempts")}:</strong> {t(language, "unlimitedAttempts")}</p>
                  </div>

                  {lastQuizResult && (
                    <Alert variant={lastQuizResult.passed ? "success" : "danger"}>
                      <strong>{t(language, "lastAttempt")}:</strong><br />
                      {t(language, "score")}: {lastQuizResult.score}/{lastQuizResult.totalQuestions} ({lastQuizResult.percentage}%)<br />
                      {t(language, "status")}: {lastQuizResult.passed ? t(language, "passed") : t(language, "failed")}<br />
                      {t(language, "date")}: {lastQuizResult.completedAt?.toDate?.().toLocaleString(language === "pt" ? "pt-BR" : "en-NZ")}
                    </Alert>
                  )}

                  <Button
                    variant="warning"
                    size="lg"
                    onClick={handleStartTraining}
                    className="w-100"
                  >
                    {quizResults.length > 0 ? t(language, "retakeTraining") : t(language, "startTraining")}
                  </Button>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} lg={6}>
              <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white">
                  <h5 className="mb-0">{t(language, "attemptHistory")}</h5>
                </Card.Header>
                <Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {quizResults.length === 0 ? (
                    <p className="text-muted text-center py-4">
                      {t(language, "noAttempts")}
                    </p>
                  ) : (
                    <div className="list-group list-group-flush">
                      {quizResults.map((result, index) => (
                        <div key={result.id} className="list-group-item px-0">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>{t(language, "attempt")} #{quizResults.length - index}</strong>
                              <br />
                              <small className="text-muted">
                                {result.completedAt?.toDate?.().toLocaleString(language === "pt" ? "pt-BR" : "en-NZ")}
                              </small>
                            </div>
                            <div className="text-end">
                              <Badge bg={result.passed ? "success" : "danger"}>
                                {result.percentage}%
                              </Badge>
                              <br />
                              <small className="text-muted">
                                {result.score}/{result.totalQuestions}
                              </small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </Container>
  );
}
